/** Registration moves gameplay coordinates, never pixels of the authored environment. */
export interface IslandPoint {
  x: number;
  y: number;
}
export type IslandQuad = readonly [IslandPoint, IslandPoint, IslandPoint, IslandPoint];
export type IslandHomography = readonly [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
];
export interface IslandCoverRectangle {
  x: number;
  y: number;
  width: number;
  height: number;
  scale: number;
}
export interface SettlementIslandRegistration {
  districtCode: SettlementIslandDistrictCode;
  terrain: 'original' | 'expansion';
  forward: IslandHomography;
  inverse: IslandHomography;
  cover: IslandCoverRectangle;
  targetQuad: IslandQuad;
  sourceQuad: IslandQuad;
}

/** Source dimensions and primary calibration remain stable across expansion unlocks. */
export const SETTLEMENT_ISLAND_ART = {
  source: 'settlement-basin-v1.png',
  width: 1672,
  height: 941,
  primaryDistrictCode: 'lakeside_outpost',
  // Back, right, front, left: safely inside grass/stone, excluding shoreline bridges.
  primaryQuad: [
    { x: 0.48, y: 0.36 },
    { x: 0.66, y: 0.46 },
    { x: 0.49, y: 0.6 },
    { x: 0.29, y: 0.48 },
  ],
} as const;

/**
 * Ground quads in the original image coordinate system, ordered back/right/front/left.
 * Only primary is original bare land. Expansion quads require separately rendered organic
 * terrain after authoritative unlock; they must never imply existing background land.
 */
export const SETTLEMENT_ISLAND_DISTRICTS = {
  lakeside_outpost: {
    terrain: 'original',
    sourceQuad: SETTLEMENT_ISLAND_ART.primaryQuad.map(({ x, y }) => ({
      x: x * 1672,
      y: y * 941,
    })) as unknown as IslandQuad,
    basis: 'central grass island; excludes shoreline rocks and connecting bridges',
  },
  garden_quarter: {
    terrain: 'expansion',
    sourceQuad: [
      { x: 320, y: 640 },
      { x: 480, y: 696 },
      { x: 192, y: 796 },
      { x: 32, y: 740 },
    ],
    basis: 'runtime organic lower-left expansion land, rendered only after district unlock',
  },
  artisan_quarter: {
    terrain: 'expansion',
    sourceQuad: [
      { x: 230, y: 260 },
      { x: 518, y: 360 },
      { x: 358, y: 416 },
      { x: 70, y: 316 },
    ],
    basis: 'runtime organic upper-left expansion land, rendered only after district unlock',
  },
  market_quarter: {
    terrain: 'expansion',
    sourceQuad: [
      { x: 1460, y: 340 },
      { x: 1620, y: 396 },
      { x: 1332, y: 496 },
      { x: 1172, y: 440 },
    ],
    basis: 'runtime organic upper-right expansion land, rendered only after district unlock',
  },
  ceremonial_quarter: {
    terrain: 'expansion',
    sourceQuad: [
      { x: 1290, y: 650 },
      { x: 1578, y: 750 },
      { x: 1418, y: 806 },
      { x: 1130, y: 706 },
    ],
    basis: 'runtime organic foreground-right expansion land, rendered only after district unlock',
  },
} as const satisfies Record<
  string,
  { sourceQuad: IslandQuad; basis: string; terrain: 'original' | 'expansion' }
>;
export type SettlementIslandDistrictCode = keyof typeof SETTLEMENT_ISLAND_DISTRICTS;

/** Calibration-domain check; do not clip invalid placement previews, which must remain visible. */
export function islandQuadContains(quad: IslandQuad, point: IslandPoint): boolean {
  if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return false;
  const turns = quad.map((a, index) => {
    const b = quad[(index + 1) % 4]!;
    return (b.x - a.x) * (point.y - a.y) - (b.y - a.y) * (point.x - a.x);
  });
  const tolerance =
    1e-7 *
    Math.max(
      1,
      ...quad.map((a, index) => {
        const b = quad[(index + 1) % 4]!;
        return Math.hypot(b.x - a.x, b.y - a.y);
      }),
    );
  return turns.every((value) => value >= -tolerance) || turns.every((value) => value <= tolerance);
}

/** Identical to the renderer's centered cover image; includes its off-screen image extents. */
export function settlementIslandCover(
  width: number,
  height: number,
): IslandCoverRectangle | undefined {
  if (![width, height].every(Number.isFinite) || width <= 0 || height <= 0) return undefined;
  const scale = Math.max(
    width / SETTLEMENT_ISLAND_ART.width,
    height / SETTLEMENT_ISLAND_ART.height,
  );
  const imageWidth = SETTLEMENT_ISLAND_ART.width * scale;
  const imageHeight = SETTLEMENT_ISLAND_ART.height * scale;
  return {
    x: (width - imageWidth) / 2,
    y: (height - imageHeight) / 2,
    width: imageWidth,
    height: imageHeight,
    scale,
  };
}

function convex(quad: IslandQuad): boolean {
  if (!quad.every((point) => Number.isFinite(point.x) && Number.isFinite(point.y))) return false;
  const turns = quad.map((a, index) => {
    const b = quad[(index + 1) % 4]!,
      c = quad[(index + 2) % 4]!;
    return (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
  });
  return turns.every((value) => value > 1e-9) || turns.every((value) => value < -1e-9);
}

/** Four correspondences determine a projective plane transform. No raster resampling. */
export function islandHomography(from: IslandQuad, to: IslandQuad): IslandHomography | undefined {
  if (!convex(from) || !convex(to)) return undefined;
  const rows = from.flatMap(({ x, y }, index) => {
    const target = to[index]!;
    return [
      [x, y, 1, 0, 0, 0, -target.x * x, -target.x * y, target.x],
      [0, 0, 0, x, y, 1, -target.y * x, -target.y * y, target.y],
    ];
  });
  for (let column = 0; column < 8; column++) {
    let pivot = column;
    for (let row = column + 1; row < 8; row++)
      if (Math.abs(rows[row]![column]!) > Math.abs(rows[pivot]![column]!)) pivot = row;
    if (Math.abs(rows[pivot]![column]!) < 1e-10) return undefined;
    [rows[column], rows[pivot]] = [rows[pivot]!, rows[column]!];
    const divisor = rows[column]![column]!;
    for (let entry = column; entry <= 8; entry++)
      rows[column]![entry] = rows[column]![entry]! / divisor;
    for (let row = 0; row < 8; row++) {
      if (row === column) continue;
      const factor = rows[row]![column]!;
      for (let entry = column; entry <= 8; entry++)
        rows[row]![entry] = rows[row]![entry]! - factor * rows[column]![entry]!;
    }
  }
  const result = [...rows.map((row) => row[8]!), 1] as unknown as IslandHomography;
  return result.every(Number.isFinite) ? result : undefined;
}

export function applyIslandHomography(
  matrix: IslandHomography,
  point: IslandPoint,
): IslandPoint | undefined {
  const [a, b, c, d, e, f, g, h, i] = matrix;
  const denominator = g * point.x + h * point.y + i;
  if (!Number.isFinite(denominator) || Math.abs(denominator) < 1e-9) return undefined;
  const x = (a * point.x + b * point.y + c) / denominator;
  const y = (d * point.x + e * point.y + f) / denominator;
  return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : undefined;
}

/** Magnitude of dH/dx: scale upright art uniformly; do not apply ground shear to sprites. */
export function islandLateralScale(
  matrix: IslandHomography,
  point: IslandPoint,
): number | undefined {
  const [a, b, c, d, e, f, g, h, i] = matrix;
  const denominator = g * point.x + h * point.y + i;
  if (!Number.isFinite(denominator) || Math.abs(denominator) < 1e-9) return undefined;
  const dx = (a * denominator - g * (a * point.x + b * point.y + c)) / denominator ** 2;
  const dy = (d * denominator - g * (d * point.x + e * point.y + f)) / denominator ** 2;
  const scale = Math.hypot(dx, dy);
  return Number.isFinite(scale) && scale > 0 ? scale : undefined;
}

/** Source quad is the existing pinhole-projected district, before any grid-only view transform. */
export function createSettlementIslandRegistration(
  projectedDistrict: IslandQuad,
  width: number,
  height: number,
  districtCode = 'lakeside_outpost',
): SettlementIslandRegistration | undefined {
  if (!Object.hasOwn(SETTLEMENT_ISLAND_DISTRICTS, districtCode)) return undefined;
  const code = districtCode as SettlementIslandDistrictCode;
  const cover = settlementIslandCover(width, height);
  if (!cover) return undefined;
  const targetQuad = SETTLEMENT_ISLAND_DISTRICTS[code].sourceQuad.map((point) => ({
    x: cover.x + point.x * cover.scale,
    y: cover.y + point.y * cover.scale,
  })) as unknown as IslandQuad;
  const forward = islandHomography(projectedDistrict, targetQuad);
  const inverse = islandHomography(targetQuad, projectedDistrict);
  return forward && inverse
    ? {
        districtCode: code,
        terrain: SETTLEMENT_ISLAND_DISTRICTS[code].terrain,
        forward,
        inverse,
        cover,
        targetQuad,
        sourceQuad: projectedDistrict,
      }
    : undefined;
}
